import SvgPinIcon from 'ext-ui/svg/icon/SvgPinIcon'
import React, { useId } from 'react'
import { canRenderReportingChart } from 'src/agent/components/ReportingChartRenderer/ReportingChartRenderer'
import type { AskReportingChartQuestion } from 'src/agent/reporting/chartQuestion.types'
import type { ReportingArtifact } from 'src/agent/reporting/reportingPresentation.types'
import { useTranslate } from 'src/localization/Trns'

import ChartQuestionPopover from '../ChartQuestionPopover/ChartQuestionPopover'
import ReportingCanvas from '../ReportingCanvas/ReportingCanvas'
import {
  ArtifactCardHeader,
  ArtifactCardHeading,
  ArtifactCardShell,
  ArtifactCardTitleRow,
  PinButton,
} from './ReportingArtifactCard.style'

export interface ReportingArtifactCardProps {
  artifact: ReportingArtifact
  onPin?: (artifact: ReportingArtifact) => void
  isPinned?: boolean
  isPinning?: boolean
  isPinDisabled?: boolean
  layout?: 'inline' | 'pinned'
  onAskQuestion?: AskReportingChartQuestion
  questionContextLabel?: string
}

const ReportingArtifactCard: React.FC<ReportingArtifactCardProps> = ({
  artifact,
  onPin,
  isPinned = false,
  isPinning = false,
  isPinDisabled = false,
  layout = 'inline',
  onAskQuestion,
  questionContextLabel,
}) => {
  const translate = useTranslate()
  const titleId = useId()
  const pinLabel = isPinned
    ? translate('Pinned to Reporting')
    : translate('Pin to Reporting')
  const pinDisabled =
    isPinned ||
    isPinning ||
    isPinDisabled ||
    !artifact.liveReportCandidateId ||
    !onPin
  const sourceContext = artifact.sourceContext
  const hasQuestionAuthority =
    (sourceContext?.kind === 'live_report' &&
      Boolean(sourceContext.reportId && sourceContext.analysisContextToken)) ||
    (sourceContext?.kind === 'artifact' &&
      Boolean(sourceContext.originConversationId))
  const canAskQuestion =
    canRenderReportingChart(artifact.chartSpec, artifact.dataset) &&
    hasQuestionAuthority

  return (
    <ArtifactCardShell
      aria-labelledby={titleId}
      data-testid="reporting-artifact-card"
      $pinned={layout === 'pinned'}
    >
      <ArtifactCardHeader>
        <ArtifactCardTitleRow>
          <ArtifactCardHeading id={titleId}>
            {artifact.title}
          </ArtifactCardHeading>
          {canAskQuestion && onAskQuestion && questionContextLabel && (
            <ChartQuestionPopover
              chartTitle={artifact.title}
              contextLabel={questionContextLabel}
              onSubmit={(question): void =>
                onAskQuestion(
                  artifact.sourceContext?.kind === 'live_report'
                    ? {
                        source: 'live_report',
                        key: `live-report:${artifact.sourceContext.reportId}`,
                        title: artifact.title,
                        contextLabel: questionContextLabel,
                        artifact,
                        reportId: artifact.sourceContext.reportId,
                        analysisContextToken:
                          artifact.sourceContext.analysisContextToken,
                      }
                    : {
                        source: 'artifact',
                        key: `artifact:${artifact.id}`,
                        title: artifact.title,
                        contextLabel: questionContextLabel,
                        artifact,
                      },
                  question,
                )
              }
            />
          )}
        </ArtifactCardTitleRow>
        {onPin && (
          <PinButton
            type="button"
            aria-label={pinLabel}
            aria-pressed={isPinned}
            disabled={pinDisabled}
            onClick={(): void => onPin(artifact)}
          >
            <SvgPinIcon filled={isPinned} aria-hidden />
          </PinButton>
        )}
      </ArtifactCardHeader>
      <ReportingCanvas
        artifact={artifact}
        presentation="embedded"
        fitToContainer={layout === 'pinned'}
      />
    </ArtifactCardShell>
  )
}

export { ReportingArtifactCard }
export default ReportingArtifactCard
